import { useSubmit } from "@hyper-fetch/react";
import { ArrowUpRight, Check } from "lucide-react";
import { useId, useState, type FormEvent } from "react";

import { sdk } from "@/api/sdk";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { m } from "@/paraglide/messages.js";
import { ContactApp, ContactTopic } from "@complete-athlete/enums";

/** What someone running a business writes about, deciding whether to sign up included. */
export const CONTACT_TOPICS = [
  ContactTopic.SUPPORT,
  ContactTopic.SALES,
  ContactTopic.BILLING,
  ContactTopic.PRIVACY,
  ContactTopic.OTHER,
] as const;

const topicLabel: Record<(typeof CONTACT_TOPICS)[number], () => string> = {
  [ContactTopic.SUPPORT]: () => m.contact_topic_support(),
  [ContactTopic.SALES]: () => m.contact_topic_sales(),
  [ContactTopic.BILLING]: () => m.contact_topic_billing(),
  [ContactTopic.PRIVACY]: () => m.contact_topic_privacy(),
  [ContactTopic.OTHER]: () => m.contact_topic_other(),
};

const field = "h-12 rounded-2xl border-border bg-background px-4 text-[15px]";

type Draft = { topic: (typeof CONTACT_TOPICS)[number]; name: string; email: string; message: string; website: string };
const EMPTY: Draft = { topic: ContactTopic.SUPPORT, name: "", email: "", message: "", website: "" };

/**
 * A message to the people who run the platform. Sent, it turns into a note saying where the
 * answer will come; refused (too many in an hour, or the server is down), it keeps every word
 * typed and says why under the button.
 */
export const ContactForm = () => {
  const [draft, setDraft] = useState<Draft>(EMPTY);
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const send = useSubmit(sdk.contact.$post);
  const id = useId();

  const set = <Key extends keyof Draft>(key: Key, value: Draft[Key]) => setDraft((current) => ({ ...current, [key]: value }));

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setFailure(null);
    const { data, error } = await send.submit({ payload: { app: ContactApp.COACH, ...draft } });
    if (error || !data) {
      setFailure(error?.message || m.contact_form_error());
      return;
    }
    setSentTo(draft.email);
    setDraft((current) => ({ ...EMPTY, name: current.name, email: current.email }));
  };

  if (sentTo) {
    return (
      <div role="status" className="grid min-h-[420px] place-content-center justify-items-center gap-4 rounded-[32px] border border-border bg-background p-8 text-center">
        <span className="grid size-14 place-items-center rounded-full bg-primary text-primary-foreground">
          <Check aria-hidden className="size-6" />
        </span>
        <h2 className="text-[clamp(1.5rem,2.6vw,2rem)] leading-[1.1] font-medium tracking-[-0.03em]">{m.contact_form_sent_title()}</h2>
        <p className="max-w-[40ch] text-[15px] leading-normal text-muted-foreground">{m.contact_form_sent_body({ email: sentTo })}</p>
        <button
          type="button"
          onClick={() => setSentTo(null)}
          className="mt-2 inline-flex h-10 items-center rounded-full border border-border px-5 text-sm font-medium transition-colors hover:bg-muted"
        >
          {m.contact_form_another()}
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} className="relative grid gap-6 rounded-[32px] border border-border bg-background p-6 md:p-9">
      <fieldset>
        <legend className="mb-3 text-[13px] font-medium text-muted-foreground">{m.contact_form_topic()}</legend>
        <div className="flex flex-wrap gap-2">
          {CONTACT_TOPICS.map((topic) => (
            <label
              key={topic}
              className={cn(
                "inline-flex h-10 cursor-pointer items-center rounded-full border px-4 text-sm font-medium transition-colors has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-ring",
                draft.topic === topic ? "border-foreground bg-foreground text-background" : "border-border hover:bg-muted",
              )}
            >
              <input
                type="radio"
                name={`${id}-topic`}
                value={topic}
                checked={draft.topic === topic}
                onChange={() => set("topic", topic)}
                className="sr-only"
              />
              {topicLabel[topic]()}
            </label>
          ))}
        </div>
      </fieldset>

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="grid gap-2">
          <Label htmlFor={`${id}-name`}>{m.contact_form_name()}</Label>
          <Input
            id={`${id}-name`}
            required
            maxLength={120}
            autoComplete="name"
            value={draft.name}
            onChange={(event) => set("name", event.target.value)}
            inputClassName={field}
          />
        </div>
        <div className="grid gap-2">
          <Label htmlFor={`${id}-email`}>{m.contact_form_email()}</Label>
          <Input
            id={`${id}-email`}
            type="email"
            required
            maxLength={254}
            autoComplete="email"
            value={draft.email}
            onChange={(event) => set("email", event.target.value)}
            inputClassName={field}
          />
        </div>
      </div>

      <div className="grid gap-2">
        <Label htmlFor={`${id}-message`}>{m.contact_form_message()}</Label>
        <Textarea
          id={`${id}-message`}
          required
          minLength={10}
          maxLength={5000}
          rows={6}
          placeholder={m.contact_form_message_placeholder()}
          value={draft.message}
          onChange={(event) => set("message", event.target.value)}
          className="min-h-40 rounded-2xl border-border bg-background px-4 py-3 text-[15px] md:text-[15px]"
        />
      </div>

      {/* Kept off screen and out of the tab order: only a script fills it. */}
      <div aria-hidden className="absolute -left-[9999px] h-px w-px overflow-hidden">
        <label>
          Website
          <input tabIndex={-1} autoComplete="off" value={draft.website} onChange={(event) => set("website", event.target.value)} />
        </label>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-4">
        <p className="max-w-[40ch] text-[13px] text-muted-foreground">{m.contact_form_reply_note()}</p>
        <button
          type="submit"
          disabled={send.submitting}
          className="group inline-flex items-center gap-1 rounded-full outline-none focus-visible:ring-2 focus-visible:ring-ring active:scale-[0.98] disabled:opacity-60"
        >
          <span className="inline-flex h-11 items-center rounded-full bg-foreground px-6 text-sm font-medium text-background">
            {send.submitting ? m.contact_form_sending() : m.contact_form_send()}
          </span>
          <span className="grid size-11 place-items-center rounded-full bg-foreground text-background transition-transform duration-200 group-hover:rotate-45">
            <ArrowUpRight aria-hidden className="size-4" />
          </span>
        </button>
      </div>
      {failure ? (
        <p role="alert" className="-mt-2 text-sm text-destructive">
          {failure}
        </p>
      ) : null}
    </form>
  );
};
